'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Icons } from '@/components/ui/icons'
import Link from 'next/link'

interface VideoCallLandingProps {
  participantName: string
  setParticipantName: (name: string) => void
  dealerLocation: string
  setDealerLocation: (location: string) => void
  onStartCall: () => void
  isLoading: boolean
  error: string
  setError: (error: string) => void
}

export function VideoCallLanding({
  participantName,
  setParticipantName,
  dealerLocation,
  setDealerLocation,
  onStartCall,
  isLoading,
  error,
  setError,
}: VideoCallLandingProps) {
  return (
    <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center bg-gradient-to-br from-brand-cornflower/10 via-brand-sky/10 to-brand-periwinkle/10 p-4">
      <div className="w-full max-w-lg">
        <Card className="shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-cornflower to-brand-periwinkle shadow-lg">
              <Icons.video className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Adani Cement Sales Connect</CardTitle>
            <CardDescription>
              Connect with Priya, our AI Sales Expert, to discuss exclusive offers, schemes, and personalized support for your cement business.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Info Box */}
            <Alert className="border-brand-cornflower/20 bg-brand-cornflower/5">
              <Icons.info className="h-4 w-4 text-brand-cornflower" />
              <AlertDescription>
                <strong className="text-brand-navy">Conversation will start in Hindi</strong>
                <p className="mt-1 text-sm text-muted-foreground">
                  Priya will automatically switch to your preferred language (Hindi/English/Hinglish)
                </p>
              </AlertDescription>
            </Alert>

            {/* Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Dealer Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="name"
                  placeholder="Enter dealer name (e.g., Rajesh Kumar)"
                  value={participantName}
                  onChange={(e) => {
                    setParticipantName(e.target.value)
                    setError('')
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="location" className="text-sm font-medium">
                  Location <span className="text-muted-foreground">(Optional)</span>
                </label>
                <Input
                  id="location"
                  placeholder="Enter location (e.g., Mumbai, Delhi)"
                  value={dealerLocation}
                  onChange={(e) => {
                    setDealerLocation(e.target.value)
                    setError('')
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && onStartCall()}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <Icons.alertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={onStartCall}
                disabled={isLoading || !participantName.trim()}
                className="w-full"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Icons.loader className="mr-2 h-5 w-5 animate-spin" />
                    Connecting to Priya...
                  </>
                ) : (
                  <>
                    <Icons.video className="mr-2 h-5 w-5" />
                    Start Video Call with AI Expert
                  </>
                )}
              </Button>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-3 border-t pt-6">
              <FeatureCard icon={Icons.zap} title="Zero Lifter Reactivation" />
              <FeatureCard icon={Icons.sparkles} title="Exclusive Schemes" />
              <FeatureCard icon={Icons.brain} title="Emotion Detection" />
              <FeatureCard icon={Icons.users} title="TSO Support Available" />
            </div>

            {/* TSO Access Link */}
            <div className="border-t pt-6 text-center">
              <Link
                href="/video-call?view=tso"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-cornflower hover:text-brand-navy"
              >
                <Icons.users className="h-4 w-4" />
                TSO Dashboard Access
                <Icons.externalLink className="h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function FeatureCard({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border bg-card p-4 text-center transition-colors hover:bg-accent">
      <Icon className="h-6 w-6 text-brand-cornflower" />
      <span className="text-xs font-medium">{title}</span>
    </div>
  )
}
